import React, { useState, useRef, useEffect } from "react";
import { Icon, Input } from "semantic-ui-react";
import { useSite } from "../../../../contexts";
import { useTemplateCatalog, TemplateCard } from "../ChecklistTemplateCatalog";
import { useChecklistSiteIndustry } from "../useChecklistSiteIndustry";
import { NewChecklistButton } from "../NewChecklistButton";
import { NORM_COUNTRIES } from "../normCountries";
import "./ChecklistExplore.scss";

// Mismo patrón que el filtro "Estado"/"Sitio" de Requisitos Legales (ver
// FilterDropdown en LegalRequirementsTable.js): al abrirlo despliega un
// menú propio con las opciones como pills blancas, no el dropdown nativo
// del navegador (un <select> no se puede estilar así al abrirse).
function NormaFilterDropdown({ options, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className="cxp-fd" ref={ref}>
      <button type="button" className="cxp__norma-select" onClick={() => setOpen((v) => !v)}>
        <span>{selected ? selected.text : placeholder}</span>
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`cxp-fd-arrow${open ? " cxp-fd-arrow--open" : ""}`}>
          <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7" />
        </svg>
      </button>
      {open && (
        <div className="cxp-fd-menu">
          {options.map((o) => (
            <button
              type="button"
              key={o.key}
              className={`cxp-fd-item${o.value === value ? " cxp-fd-item--active" : ""}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ChecklistExplore({ onSelectTemplate, onCreateTemplate, country, onCountryChange }) {
  const { site: activeSite } = useSite();
  const [siteIndustry] = useChecklistSiteIndustry(activeSite);

  // La industria del sitio ya scopea el catálogo acá (sin control visible —
  // se asigna desde el tab Plantillas). Sin filtro manual de industria en
  // esta pantalla, tal como el diseño original.
  const { templates, normRefs, search, setSearch, loading } = useTemplateCatalog(siteIndustry);

  // TEMPORAL: el filtro de país todavía no se aplica al catálogo — normas
  // recién cargadas en Catálogos no estaban apareciendo acá como opción
  // (normRefs quedando desincronizado), así que por ahora el dropdown no
  // esconde checklists mientras se resuelve esa desincronización. El select
  // queda visible/elegible pero no filtra `templatesWithNorm`.
  const templatesWithNorm = templates;

  return (
    <div className="cxp">
      {/* Franja gris (Rectangle 201) — ya no envuelve solo los filtros,
          se estira hacia abajo para contener también las cards de checklists
          con norma, ajustándose al tamaño que tengan. */}
      <div className="cxp__panel">
        <div className="cxp__filters">
          <Input
            icon="search"
            iconPosition="left"
            placeholder="Buscar"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="cxp__search"
            loading={loading}
          />
          <NormaFilterDropdown
            options={NORM_COUNTRIES.map((c) => ({ key: c.value, value: c.value, text: c.text }))}
            value={country}
            onChange={(v) => onCountryChange?.(v)}
            placeholder="Norma"
          />
          {onCreateTemplate && (
            <NewChecklistButton className="cxp__new-btn" onSelect={onCreateTemplate} align="right">
              Crear checklist propio
              <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className="cxp__new-btn__arrow">
                <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7" />
              </svg>
            </NewChecklistButton>
          )}
        </div>

        {loading ? (
          <div className="ctpl__loading">Cargando checklists...</div>
        ) : templatesWithNorm.length === 0 ? (
          <div className="ctpl__empty">
            <Icon name="clipboard outline" size="large" />
            <p>
              {search
                ? "No se encontraron checklists con esos filtros."
                : "No hay checklists disponibles."}
            </p>
          </div>
        ) : (
          <div className="cxp__grid">
            {templatesWithNorm.map((tpl) => (
              <TemplateCard
                key={tpl._id}
                tpl={tpl}
                selected={false}
                onClick={onSelectTemplate}
                country={country}
                normRefs={normRefs}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
